'use client';

import React from 'react';
import {
  Box,
  BoxProps,
  Container,
  Divider,
  Group,
  Text,
  useMantineTheme,
} from '@mantine/core';
import LogoImage from '../../public/img/logo.svg';
import FAIcon, { ValidIconName } from './FAIcon';
import OpacityLink from './OpacityLink';
import { useResponsive } from '@/hooks/use-responsive';
import { useAuthContext } from '@/context/Auth';
import { useCurrentUser } from '@/hooks/use-current-user';
import styled from '@emotion/styled';
import type { MenuItem } from '@/services/menuServerService';

interface SiteFooterClientProps extends BoxProps {
  initialMenuList: MenuItem[];
  className?: string;
}

interface SocialLink {
  icon: ValidIconName;
  href: string;
}

/**
 * Styled components for footer menu
 */
const FooterMenuWrapper = styled.div`
  display: flex;
  gap: 30px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
`;

const FooterMenuGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  @media screen and (max-width: 768px) {
    align-items: center;
  }
`;

const FooterMenuButton = styled.button<{ isParent?: boolean }>`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: white;
  font-size: 14px;
  font-weight: ${({ isParent }) => (isParent ? 600 : 500)};
  opacity: ${({ isParent }) => (isParent ? 1 : 0.8)};
  transition: opacity 200ms ease;

  &:hover {
    opacity: 1;
  }
`;

const FooterChildItem = styled.button`
  background: none;
  border: none;
  padding: 0;
  padding-left: 12px;
  cursor: pointer;
  color: white;
  font-size: 13px;
  font-weight: 400;
  opacity: 0.7;
  transition: opacity 200ms ease;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 4px;
    background-color: var(--color-primary, #007bff);
    border-radius: 50%;
  }

  &:hover {
    opacity: 1;
  }

  @media screen and (max-width: 768px) {
    padding-left: 16px;
  }
`;

const SiteFooterClient: React.FC<SiteFooterClientProps> = ({
  initialMenuList,
  className,
  ...props
}) => {
  const { responsive } = useResponsive();
  const theme = useMantineTheme();
  const {
    state: { twoFactorAuthenticated },
    dispatch,
  } = useAuthContext();
  const [currentUser, authStatus] = useCurrentUser();

  // Use server-provided data (no loading state needed!)
  const nestedMenuList = initialMenuList;

  const handleLoginClick = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch({ type: 'OPEN_MODAL' });
  };

  /**
   * Handle menu item click
   */
  const handleMenuItemClick = (menuItem: MenuItem) => {
    if (menuItem.url) {
      if (menuItem.target === '_blank') {
        window.open(menuItem.url, '_blank');
      } else {
        window.open(menuItem.url, '_self');
      }
    }
  };

  const renderMenuItem = (menuItem: MenuItem) => {
    const hasChildren = menuItem.children && menuItem.children.length > 0;
    console.log('Rendering menu item:', menuItem, 'Has children:', hasChildren);  
    if (hasChildren) {
      return (
        <FooterMenuGroup key={menuItem.id}>
          <FooterMenuButton
            isParent={true}
            style={{color : menuItem?.color || ''}}
            onClick={() => handleMenuItemClick(menuItem)}
            aria-label={menuItem.title}
          >
            {menuItem.title}
          </FooterMenuButton>
          {menuItem.children!.map((child) => (
            <FooterChildItem
              style={{color : child?.color || ''}}
              key={child.id}
              onClick={() => handleMenuItemClick(child)}
              aria-label={child.title}
            >
              {child.title}
            </FooterChildItem>
          ))}
        </FooterMenuGroup>
      );
    }

    return (
      <FooterMenuButton
        key={menuItem.id}
        style={{color : menuItem?.color || ''}}
        onClick={() => handleMenuItemClick(menuItem)}
        aria-label={menuItem.title}
      >
        {menuItem.title}
      </FooterMenuButton>
    );
  };

  const socialLinks: SocialLink[] = [
    {
      icon: 'linkedin-in',
      href: 'https://www.linkedin.com/company/99208016/admin/feed/posts/',
    },
    {
      icon: 'facebook',
      href: 'https://www.facebook.com/HomeHealthSolutions.io',
    },
    {
      icon: 'instagram',
      href: 'https://www.instagram.com/homehealthsolutions.io/',
    },
    {
      icon: 'twitter',
      href: 'https://twitter.com/homehealthsolutions.io/',
    },
  ];

  return (
    <Box
      component="footer"
      py={100}
      style={{
        backgroundColor: theme.colors.dark[7],
      }}
      className={className}
      {...props}
    >
      <Container size="xl">
        <Group
          wrap="nowrap"
          justify="space-between"
          gap={responsive({
            xs: 30,
            md: 0,
          })}
          style={{
            flexDirection: responsive({
              xs: 'column',
              md: 'row',
            }),
          }}
        >
          {/* Logo */}
          <Box c="white" w={200}>
            <LogoImage />
          </Box>

          {/* Navigation Links */}
          <Group
            gap={30}
            style={{
              flexDirection: responsive({
                xs: 'column',
                sm: 'row',
              }),
            }}
          >
            <FooterMenuWrapper>
              {nestedMenuList.map((menuItem) => renderMenuItem(menuItem))}
            </FooterMenuWrapper>

            {/* Conditional Login/Account Link */}
            {currentUser && twoFactorAuthenticated ? (
              <OpacityLink href="/profile" ariaLabel="Profile">
                <Text c="white" fz={14} fw={500}>
                  Account
                </Text>
              </OpacityLink>
            ) : (
              <Text
                c="white"
                fz={14}
                fw={500}
                onClick={handleLoginClick}
                style={{ cursor: 'pointer' }}
              >
                Login
              </Text>
            )}
          </Group>

          {/* Social Links */}
          <Group
            gap={20}
            w={responsive({
              xs: 'auto',
              lg: 200,
            })}
            justify={responsive({
              xs: 'center',
              md: 'flex-end',
            })}
          >
            {socialLinks.map((link) => (
              <OpacityLink
                key={link.icon}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                transition={false}
                ariaLabel={link.href}
              >
                <FAIcon
                  name={link.icon}
                  prefix="fab"
                  size={16}
                  style={{
                    color: 'white',
                  }}
                />
              </OpacityLink>
            ))}
          </Group>
        </Group>

        <Divider my={50} opacity={0.1} />

        {/* Bottom Section */}
        <Group
          gap={responsive({
            xs: 20,
            md: 50,
          })}
          justify="center"
          style={{
            flexDirection: responsive({
              xs: 'column',
              md: 'row',
            }),
          }}
        >
          <Text fz={14} c="dark.1">
            © {new Date().getFullYear()} HomeHealthSolutions. All rights reserved.
          </Text>

          <OpacityLink href="/privacy" ariaLabel="Privacy Policy">
            <Text fz={14} c="white">
              Privacy Policy
            </Text>
          </OpacityLink>

          <OpacityLink href="/terms" ariaLabel="Terms of Service">
            <Text fz={14} c="white">
              Terms of Service
            </Text>
          </OpacityLink>
        </Group>
      </Container>
    </Box>
  );
};

export default SiteFooterClient;